/**
 * Utilitário para conversão de timezones entre UTC e -3h (Brasília)
 * Garante que os agendamentos sejam sempre exibidos com a hora correta -3h
 */

/**
 * Converte data local (GMT-3) para UTC para enviar à API
 * Quando o usuário seleciona "14:00" no calendário local (GMT-3),
 * queremos enviar como "17:00Z" UTC (14:00 + 3 horas = 17:00 UTC)
 *
 * @param {Date} localDate - Data no fuso horário local (GMT-3)
 * @returns {string} - Data em formato ISO UTC (com Z)
 */
export const convertLocalToUTC = localDate => {
  if (!localDate) return null

  // Se for string, converter para Date
  let dateObj = localDate
  if (typeof localDate === 'string') {
    dateObj = new Date(localDate)
  } else if (!(localDate instanceof Date)) {
    return null
  }

  // Validar se a data é válida
  if (Number.isNaN(dateObj.getTime())) {
    return null
  }

  // Converter para ISO string UTC
  // toISOString() converte automaticamente para UTC
  return dateObj.toISOString()
}

/**
 * Converte data UTC (da API) para exibição no calendário local (-3h)
 * A API retorna "2025-10-06T23:00:00Z" (23h UTC)
 * Queremos exibir como "20:00" (23h - 3 horas = 20h GMT-3) no calendário
 *
 * IMPORTANTE: O JavaScript nativo já converte automaticamente para o timezone local
 * do navegador quando você cria um Date a partir de uma string ISO com Z.
 * Mas para garantir -3h (Brasília), usamos este método confiável.
 *
 * @param {string} utcDateString - Data em formato ISO UTC+0 (com Z)
 * @returns {Date} - Objeto Date que será interpretado como local pelo FullCalendar
 */
export const convertUTCToLocal = utcDateString => {
  if (!utcDateString) return null

  // Criar objeto Date a partir da string UTC
  // Quando passamos uma string ISO com Z, new Date() já interpreta como UTC
  const utcDate = new Date(utcDateString)

  // Validar se a data é válida
  if (Number.isNaN(utcDate.getTime())) {
    return null
  }

  // O navegador e FullCalendar automaticamente convertem para o timezone local
  // Se o navegador estiver configurado para -3h, esta conversão será correta
  // Retornar o objeto Date que FullCalendar entenderá como local
  return utcDate
}

/**
 * Converte data UTC para string no formato local com -3h
 * Útil para exibir data/hora em textos ou inputs que não usam FullCalendar
 *
 * @param {string} utcDateString - Data em formato ISO UTC+0 (com Z)
 * @param {string} format - Formato desejado (padrão: 'DD/MM/YYYY HH:mm')
 * @returns {string} - Data formatada no timezone -3h
 */
export const formatUTCToLocalTimezone = (utcDateString, format = 'DD/MM/YYYY HH:mm') => {
  if (!utcDateString) return ''

  const utcDate = new Date(utcDateString)

  if (Number.isNaN(utcDate.getTime())) {
    return ''
  }

  // Criar offset -3h (180 minutos)
  const offsetMs = -3 * 60 * 60 * 1000 // -3 horas em ms
  const localDate = new Date(utcDate.getTime() + offsetMs)

  // Formatar a data baseado no formato solicitado
  if (format === 'DD/MM/YYYY HH:mm') {
    const day = String(localDate.getUTCDate()).padStart(2, '0')
    const month = String(localDate.getUTCMonth() + 1).padStart(2, '0')
    const year = localDate.getUTCFullYear()
    const hours = String(localDate.getUTCHours()).padStart(2, '0')
    const minutes = String(localDate.getUTCMinutes()).padStart(2, '0')

    return `${day}/${month}/${year} ${hours}:${minutes}`
  }

  return utcDate.toISOString()
}

/**
 * Obtém o offset UTC da data local em ms
 * Útil para operações de cálculo com timezones
 *
 * @returns {number} - Offset em milissegundos (ex: -10800000 para -3h)
 */
export const getLocalTimezoneOffset = () =>
  // Retornar offset em ms
  // -3h = -3 * 60 * 60 * 1000 = -10800000
  new Date().getTimezoneOffset() * 60 * 1000

/**
 * Verifica se o navegador está configurado para -3h (Brasília)
 * @returns {boolean}
 */
export const isTimezoneCorrect = () => {
  // Timezone offset em minutos (negativo para oeste)
  // -3h = -180 minutos
  const offset = new Date().getTimezoneOffset()
  return offset === 180 // -3h em minutos
}

/**
 * Converte um objeto com datas para as respectivas datas em UTC
 * Útil para preparar dados de formulários antes de enviar à API
 *
 * @param {Object} obj - Objeto contendo campos de data
 * @param {Array} dateFields - Array com nomes dos campos que contêm datas
 * @returns {Object} - Novo objeto com datas convertidas para UTC
 */
export const convertObjectDatesToUTC = (obj, dateFields = []) => {
  const result = { ...obj }

  dateFields.forEach(field => {
    if (result[field]) {
      result[field] = convertLocalToUTC(result[field])
    }
  })

  return result
}

/**
 * Converte um objeto com datas UTC para as respectivas datas locais
 *
 * @param {Object} obj - Objeto contendo campos de data em UTC
 * @param {Array} dateFields - Array com nomes dos campos que contêm datas UTC
 * @returns {Object} - Novo objeto com datas convertidas para local
 */
export const convertObjectDatesFromUTC = (obj, dateFields = []) => {
  const result = { ...obj }

  dateFields.forEach(field => {
    if (result[field]) {
      result[field] = convertUTCToLocal(result[field])
    }
  })

  return result
}

export default {
  convertLocalToUTC,
  convertUTCToLocal,
  formatUTCToLocalTimezone,
  getLocalTimezoneOffset,
  isTimezoneCorrect,
  convertObjectDatesToUTC,
  convertObjectDatesFromUTC,
}
